import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import Loadable from 'react-loadable';

import { Brand, Site } from '@swf/core/enums';
import { canUseDOM } from '@swf/core/utils';

interface Styles {
  theme: string;
  darkTheme: string;
}

export interface Props {
  onThemeChange: (theme: string, darkTheme?: string) => void;
}

const Theme = ({ theme, darkTheme, onThemeChange }: Styles & Props) => {
  useEffect(() => {
    onThemeChange(theme, darkTheme);
  }, [theme, darkTheme, onThemeChange]);

  // Ensure theme is set on SSR render
  if (!canUseDOM) {
    return <BodyClassName className={theme} />;
  }

  return null;
};

const render = ({ theme, darkTheme }: Styles, { onThemeChange }: Props) => (
  <Theme theme={theme} darkTheme={darkTheme} onThemeChange={onThemeChange} />
);

const loading = () => null;

const defaultTheme = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './defaultTheme.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const fullTilt = Loadable({
  loader: () =>
    import(/* webpackChunkName: "FullTilt" */ './fullTilt.scss').then((promise) => promise.default),
  modules: ['./FullTilt'],
  loading,
  render
});

const pokerStars = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerStars.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const sport = Loadable({
  loader: () =>
    import(/* webpackChunkName: "Sports" */ './sports.scss').then((promise) => promise.default),
  modules: ['./Sports'],
  loading,
  render
});

const starsCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './starsCasino.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const multiBrandCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './multiBrandCasino.scss').then(
      (promise) => promise.default
    ),
  modules: ['./CMS'],
  loading,
  render
});

const multiBrandPoker = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './multiBrandPoker.scss').then(
      (promise) => promise.default
    ),
  modules: ['./CMS'],
  loading,
  render
});

const pokerDotNet = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerDotNet.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const deshPoker = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './deshPoker.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const deshCasino = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './deshCasino.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const pokerDotNetDE = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './pokerDotNetDE.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

const foxBet = Loadable({
  loader: () =>
    import(/* webpackChunkName: "CMS" */ './foxBet.scss').then((promise) => promise.default),
  modules: ['./CMS'],
  loading,
  render
});

export type ThemeType = typeof defaultTheme;

export const themes = {
  none: null,
  defaultTheme,
  starsCasino,
  pokerStars,
  fullTilt,
  sport,
  deshCasino,
  deshPoker,
  pokerDotNet,
  pokerDotNetDE,
  multiBrandCasino,
  multiBrandPoker,
  foxBet
};

export type GetTheme = ({
  isSiteUS,
  brand,
  site,
  region
}: {
  isSiteUS: boolean;
  brand: Brand;
  site: Site;
  region?: string;
}) => React.ComponentType<Props> & Loadable.LoadableComponent;

// TODO: This can be a hook when SCSS import is not component based and `getStyle` returns CSS className not a component
export const getTheme: GetTheme = ({ isSiteUS, brand, site, region }) => {
  switch (true) {
    case site === Site.DESH:
      return brand === Brand.PokerStarsCasino ? deshCasino : deshPoker;
    case isSiteUS && brand === Brand.PokerStarsCasino:
      return multiBrandCasino;
    case isSiteUS && brand === Brand.PokerStars:
      return multiBrandPoker;
    case site === Site.NET:
      return region === 'DE' ? pokerDotNetDE : pokerDotNet;
    case brand === Brand.BetStars:
      return sport;
    case brand === Brand.FullTilt:
      return fullTilt;
    case brand === Brand.PokerStarsCasino:
      return starsCasino;
    case brand === Brand.PokerStars:
      return pokerStars;
    case isSiteUS && brand === Brand.FoxBet:
      return foxBet;
    default:
      return defaultTheme;
  }
};
